import { Checkbox } from '@consigli/facade';
import { TENDER_PROCESSOR_CATEGORIES, ProcessorPreferenceKey, CategoryKey } from '@consigli/types';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type CategoryOptions = readonly { key: ProcessorPreferenceKey; translationKey: string }[];

interface ProcessorPreferencesProps {
  selectedProcessors: ProcessorPreferenceKey[];
  setSelectedProcessors: (value: ProcessorPreferenceKey[]) => void;
  categories?: Partial<Record<CategoryKey, CategoryOptions>>;
  categoryLabelOverrides?: Partial<Record<CategoryKey, string>>;
}

interface CategorySectionProps {
  categoryKey: CategoryKey;
  options: readonly { key: ProcessorPreferenceKey; translationKey: string }[];
  selectedProcessors: ProcessorPreferenceKey[];
  onToggle: (key: ProcessorPreferenceKey) => void;
  onSelectAll: (keys: ProcessorPreferenceKey[], selectAll: boolean) => void;
  labelOverride?: string;
}

const CategorySection = ({
  categoryKey,
  options,
  selectedProcessors,
  onToggle,
  onSelectAll,
  labelOverride,
}: CategorySectionProps) => {
  const { t } = useTranslation();

  const allSelected = options.every((opt) => selectedProcessors.includes(opt.key));
  const categoryKeys = options.map((opt) => opt.key);

  const handleSelectAllChange = useCallback(() => {
    onSelectAll(categoryKeys, !allSelected);
  }, [categoryKeys, allSelected, onSelectAll]);

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-day-neutral-dark">
          {labelOverride ?? t(`processor-preferences.category.${categoryKey}`)}
        </span>
        <Checkbox
          id={`select-all-${categoryKey}`}
          label={t('processor-preferences.select-all')}
          checked={allSelected}
          onChange={handleSelectAllChange}
        />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
        {options.map((option) => (
          <Checkbox
            key={option.key}
            id={`processor-${option.key}`}
            label={t(option.translationKey)}
            checked={selectedProcessors.includes(option.key)}
            onChange={() => onToggle(option.key)}
          />
        ))}
      </div>
    </div>
  );
};

export const ProcessorPreferences = ({
  selectedProcessors,
  setSelectedProcessors,
  categories = TENDER_PROCESSOR_CATEGORIES,
  categoryLabelOverrides,
}: ProcessorPreferencesProps) => {
  const handleToggle = useCallback(
    (key: ProcessorPreferenceKey) => {
      if (selectedProcessors.includes(key)) {
        setSelectedProcessors(selectedProcessors.filter((k) => k !== key));
      } else {
        setSelectedProcessors([...selectedProcessors, key]);
      }
    },
    [selectedProcessors, setSelectedProcessors],
  );

  const handleSelectAll = useCallback(
    (keys: ProcessorPreferenceKey[], selectAll: boolean) => {
      if (selectAll) {
        // Add all keys that aren't already selected
        const newSelected = [...selectedProcessors];
        keys.forEach((key) => {
          if (!newSelected.includes(key)) {
            newSelected.push(key);
          }
        });
        setSelectedProcessors(newSelected);
      } else {
        // Remove all keys from the category
        setSelectedProcessors(selectedProcessors.filter((k) => !keys.includes(k)));
      }
    },
    [selectedProcessors, setSelectedProcessors],
  );

  const categoryEntries = Object.entries(categories) as [CategoryKey, CategoryOptions][];

  return (
    <div>
      {categoryEntries.map(([categoryKey, options]) => (
        <CategorySection
          key={categoryKey}
          categoryKey={categoryKey}
          options={options}
          selectedProcessors={selectedProcessors}
          onToggle={handleToggle}
          onSelectAll={handleSelectAll}
          labelOverride={categoryLabelOverrides?.[categoryKey]}
        />
      ))}
    </div>
  );
};
